import React, { useState, useMemo } from 'react';
import { Calculator } from 'lucide-react';
import { Card, CardHeader } from '../shared';
import { useTheme } from '../../contexts/ThemeContext';
import { formatCurrency } from '../../utils/formatters';
import { compoundGrowth } from '../../utils/calculators';

interface CompoundInterestCalculatorProps {
  variant?: 'card' | 'page';
}

export const CompoundInterestCalculator: React.FC<CompoundInterestCalculatorProps> = ({ variant = 'card' }) => {
  const { darkMode } = useTheme();
  const [initial, setInitial] = useState(0);
  const [monthly, setMonthly] = useState(0);
  const [rate, setRate] = useState(0);
  const [yearsStr, setYearsStr] = useState('');
  const years = Math.max(Number(yearsStr) || 0, 0);

  const inputClass = `w-full px-3 py-2 rounded-lg text-sm ${
    darkMode
      ? 'bg-white/10 border border-white/10 text-white placeholder-slate-500'
      : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'
  }`;

  const labelClass = `text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;

  const results = useMemo(() => compoundGrowth(initial, monthly, rate, years), [initial, monthly, rate, years]);
  const { contributionPct } = results;

  return (
    <Card variant={variant}>
      <CardHeader
        title="Compound Interest"
        icon={<Calculator size={20} />}
        variant={variant}
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Initial Investment</label>
            <input type="number" value={initial} onChange={e => setInitial(+e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Monthly Contribution</label>
            <input type="number" value={monthly} onChange={e => setMonthly(+e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Annual Return (%)</label>
            <input type="number" value={rate} step={0.5} onChange={e => setRate(+e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Years</label>
            <input type="number" min={1} value={yearsStr} onChange={e => setYearsStr(e.target.value.replace('-', ''))} onFocus={e => e.target.select()} className={inputClass} />
          </div>
        </div>

        {/* Results */}
        <div className={`rounded-xl p-4 space-y-3 ${darkMode ? 'bg-white/5' : 'bg-violet-50/50'}`}>
          <div className="text-center">
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Future Value</p>
            <p className={`text-2xl font-bold ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
              {formatCurrency(results.futureValue)}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Contributions</p>
              <p className={`text-sm font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {formatCurrency(results.totalContributions)}
              </p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Interest Earned</p>
              <p className={`text-sm font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                {formatCurrency(results.totalInterest)}
              </p>
            </div>
          </div>

          {/* Stacked bar */}
          <div>
            <div className="flex h-3 rounded-full overflow-hidden">
              <div className={`${darkMode ? 'bg-violet-500' : 'bg-violet-400'}`} style={{ width: `${contributionPct}%` }} />
              <div className={`${darkMode ? 'bg-emerald-500' : 'bg-emerald-400'}`} style={{ width: `${100 - contributionPct}%` }} />
            </div>
            <div className="flex justify-between mt-1">
              <span className={`text-[10px] ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                Contributions {contributionPct.toFixed(0)}%
              </span>
              <span className={`text-[10px] ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                Interest {(100 - contributionPct).toFixed(0)}%
              </span>
            </div>
          </div>
        </div>

        {/* Milestones table */}
        {results.milestones.length > 0 && (
          <table className="w-full text-xs">
            <thead>
              <tr className={darkMode ? 'text-slate-500' : 'text-slate-400'}>
                <th className="text-left pb-1">Year</th>
                <th className="text-right pb-1">Contributions</th>
                <th className="text-right pb-1">Interest</th>
                <th className="text-right pb-1">Balance</th>
              </tr>
            </thead>
            <tbody>
              {results.milestones.map(m => (
                <tr key={m.year} className={`border-t ${darkMode ? 'border-white/5 text-slate-300' : 'border-slate-100 text-slate-700'}`}>
                  <td className="py-1.5">{m.year}</td>
                  <td className="text-right">{formatCurrency(m.contributions)}</td>
                  <td className={`text-right ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{formatCurrency(m.interest)}</td>
                  <td className="text-right font-medium">{formatCurrency(m.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
          Estimates only. Not financial advice. Actual results will vary.
        </p>
      </div>
    </Card>
  );
};
